import React, { useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Table from '../../core/common/dataTable/index'; 
import { useHeaderFilters } from '../../context/HeaderFilterContext';
import useDynamicPageSize from '../../useDynamicPageSize';
import Footer from '../../Footer';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import { toast, ToastContainer } from "react-toastify";
import nodeApi from "../../nodeApi";
import { useParams, useNavigate } from "react-router-dom";
import { encryptQuery,decryptQuery } from "../../utils/encryption";
import dayjs from "dayjs";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, formatDate } from "date-fns";
import CommonSelect, { Option } from "../../core/common/commonSelect";
import TextareaAutosize from 'react-textarea-autosize';
import { useSearchParams } from "react-router-dom";
import ScheduleInterviewModal from "../interviews/ScheduleInterviewModal";
import { AnyNaptrRecord } from 'dns';
import { Popover, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import PredefinedDateRanges from "../../core/common/datePicker";


const CustomTimeInput = (props: any) => {
  const { date, timeString, onChange } = props;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    const [hh, mm] = value.split(":").map(Number);
    if (isNaN(hh) || isNaN(mm)) {
      onChange(value);
      return;
    }

    const newTime = new Date(date);
    newTime.setHours(hh, mm, 0, 0);

    const now = new Date();

    // Prevent selecting past time when the selected date is today
    if (newTime.toDateString() === now.toDateString() && newTime < now) {
      return; // ignore invalid time
    }

    onChange(value);
  };

  return (
    <input
      type="time"
      className="form-control"
      value={timeString}
      onChange={handleChange}
      step={60}
    />
  );
};

interface Interviewer {
  id: number;
  round_id: number;
  interviewer_id: number;
  approval_status: string;
  proposed_datetime: string | null;
  comments: string | null;
  approved_at: string | null;
  created_at: string;
}

interface InterviewRound {
  id: number;
  interview_id: number;
  round_name: string | null;
  interview_type: string;
  scheduled_datetime: string;
  meeting_link: string;
  feedback_form_link: string;
  status: string;
  approval_summary: string | null;
  feedback_summary: string | null;
  reschedule_history: string | null;
  reminders: string | null;
  created_at: string;
  updated_at: string;
  interviewers: Interviewer[];
}

interface InterviewRecord {
  id: number;
  rounds: InterviewRound[];
  [key: string]: any; // fallback for other fields
}

type JobOpeningType = {
  id: number;
  job_profile: string;
  job_description: string;
  required_experience_months: number;
  job_location: string;
  status: string;
  deleted_at: string|null;
  created_at: string;
  updated_at: string;
};
type NumberOption = {
  value: number;
  label: string;
};

const useModalWithUrlCleanup = (hideBsModal: (modalId: string) => void) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const hideModal = (modalId: string) => {
    hideBsModal(modalId); // call the original function

    if (modalId === "interview_rounds_modal") {
      searchParams.delete("interviewId");
      setSearchParams(searchParams, { replace: true });
    }
  };

  return { hideModal };
};
function useDebounce<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return debounced;
}

const Interviews: React.FC = () => {
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = useDynamicPageSize();
   const isInitialFetch = useRef(true);
   const [totalItems, setTotalItems] = useState<number>(0);
  const [tableHeight, setTableHeight] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  // FILTER STATES
const DEFAULT_END_ISO = dayjs().format("YYYY-MM-DD");
const DEFAULT_START_ISO = dayjs().subtract(29, "day").format("YYYY-MM-DD");

const [useDateFilter, setUseDateFilter] = useState(true);
const [activeDateRange, setActiveDateRange] = useState({
  from: DEFAULT_START_ISO,
  to: DEFAULT_END_ISO,
});

const [selectedStartLabel, setSelectedStartLabel] = useState(
  dayjs(DEFAULT_START_ISO).format("DD-MMM-YYYY")
);
const [selectedEndLabel, setSelectedEndLabel] = useState(
  dayjs(DEFAULT_END_ISO).format("DD-MMM-YYYY")
);


const [jobProfile, setJobProfile] = useState<string>("ALL");
const [jobStatus, setJobStatus] = useState<string>("ALL");
const [openReasonRoundIds, setOpenReasonRoundIds] = useState<{
  [roundId: number]: boolean;
}>({});


const STATUS_OPTIONS: Option[] = [
  { value: "ALL", label: "All Statuses" },
  { value: "Scheduled", label: "Scheduled" },
  { value: "Completed", label: "Completed" },
  { value: "Cancelled", label: "Cancelled" },
  { value: "Not Attended", label: "Not Attended" },
  { value: "Shortlisted", label: "Shortlisted" },
  { value: "Not Shortlisted", label: "Not Shortlisted" },
  { value: "Rescheduled", label: "Rescheduled" },
];


  const { setFilters } = useHeaderFilters();
  const { encryptedQuery } = useParams<{ encryptedQuery?: string }>();

  const hasHydratedFromUrl = useRef(false);
  const isSyncingFromUrl = useRef(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const interviewIdFromURL = searchParams.get("interviewId"); 
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState<JobOpeningType | null>(null);
  const [interviewMode, setInterviewMode] = useState<"schedule" | "load"| "newRound">("newRound");
  const [selectedCandidate, setSelectedCandidate] = useState<any>(null);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [selectedRoundForStatus, setSelectedRoundForStatus] = useState<InterviewRound | null>(null);
  const [newStatus, setNewStatus] = useState<string | null>(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const ROUNDS_PAGE_SIZE = 5;
  const [roundPage, setRoundPage] = useState(1);
const [jobProfileOptions, setJobProfileOptions] = useState<Option[]>([
  { value: "ALL", label: "All Profiles" },
]);
const [roundStatusDraft, setRoundStatusDraft] = useState<{
  [roundId: number]: {
    status: string;
    reason: string;
    error?: string;
  };
}>({});

const refreshSelectedInterview = async (interviewId: number) => {
  try {
    const res: any = await nodeApi("/interviews/interview", {
      method: "POST",
      body: { role: userRole, userId, id: interviewId },
    });

    if (res?.data) {
      setSelectedInterview(res.data);
    }
  } catch (e) {
    console.error("Failed to refresh interview", e);
  }
};



// ---------- CONSTANTS ----------
const STATUSES = ["Completed", "Cancelled", "Not Attended", "Not Shortlisted"] as const;
const ROUND_STATUS_OPTIONS: Option[] = STATUSES
  .filter((s) => s !== "Completed" && s !== "Not Shortlisted")
  .map((s) => ({
    value: s,
    label: s,
  }));

// ---------- TYPES ----------
type StatusType = (typeof STATUSES)[number];

interface StatusTargetType {
  type: "round" | "interview";
  id: number | null;
}
interface InterviewFilters {
  searchTerm?: string | null;
  currentPage?: number;
  jobProfile?: string | null;
  jobStatus?: string | null;
  // dateRange?: [string, string] | null;
}

const getStatusOptions = () => {
  if (statusTarget.type === "round") {
    // Hide last status
    return STATUSES.slice(0, -1);
  }
  return STATUSES;
};

// ---------- STATE ----------
const [showStatusModal, setShowStatusModal] = useState<boolean>(false);
const [selectedStatus, setSelectedStatus] = useState<StatusType | "">("");
const [statusTarget, setStatusTarget] = useState<StatusTargetType>({
  type: "round",
  id: null,
});
const [reason, setReason] = useState("");
const [reasonError, setReasonError] = useState("");

const handleSave = () => {
  if (statusTarget.type === "interview" && (selectedStatus === "Not Shortlisted" || selectedStatus === "Cancelled")) {
    if (!reason.trim()) {
      setReasonError("Reason is required.");
      return;
    }
  }

  // Call your API normally
  updateStatus({
    round_id: statusTarget.type === "round" ? statusTarget.id : null,
    interview_id: statusTarget.type === "interview" ? statusTarget.id : null,
    status: selectedStatus,
    reason: reason || null,
  });
};
// const handleActivateDateFilterOnClick = () => {
//   if (useDateFilter) return;

//   const from = DEFAULT_START_ISO;
//   const to = DEFAULT_END_ISO;

//   setActiveDateRange({ from, to });
//   setSelectedStartLabel(dayjs(from).format("DD-MMM-YYYY"));
//   setSelectedEndLabel(dayjs(to).format("DD-MMM-YYYY"));
//   setUseDateFilter(true);
//   setCurrentPage(1);
// };
// const handleDateRangeChangeFilter = (startLabel: string, endLabel: string) => {
//   setUseDateFilter(true);

//   setSelectedStartLabel(startLabel);
//   setSelectedEndLabel(endLabel);

//   const date_from = dayjs(startLabel, "DD-MMM-YYYY").format("YYYY-MM-DD");
//   const date_to = dayjs(endLabel, "DD-MMM-YYYY").format("YYYY-MM-DD");

//   setActiveDateRange({ from: date_from, to: date_to });
//   setCurrentPage(1);
// };
  useLayoutEffect(() => {
    if (pageSize === 0) return; // Prevent setting height too early
    const calculateHeight = () => {
      const baseOffset = 194;
      const extraOffset = window.innerWidth <= 991 ? -43 : -81;
      const fixedOffset = baseOffset + extraOffset;

      const newHeight = window.innerHeight - fixedOffset;

      console.log('Calculated table height:', newHeight);
      console.log('fixedOffset:', fixedOffset);
      console.log('window.innerHeight:', window.innerHeight);
      console.log("totalItems :", totalItems);
      console.log("pagesize :", pageSize);
      setTableHeight(newHeight);
    };
    calculateHeight();
    window.addEventListener("resize", calculateHeight); // respond to window size changes

    return () => window.removeEventListener("resize", calculateHeight); // cleanup on unmount
  }, []);

  const [interviews, setInterviews] = useState<any[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState<any>(null);
  const [selectedInterview, setSelectedInterview] = useState<InterviewRecord | null>(null);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [selectedRound, setSelectedRound] = useState<InterviewRound | null>(null);
  const [interviewers, setInterviewers] = useState<any[]>([]);
  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const userRole = storedUser?.role_name; 
  const userId = storedUser?.id; 
  const [isAdmin, setisAdmin] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [interviewId, setInterviewId] = useState();
  const [rescheduleData, setRescheduleData] = useState({
    round_id: null,
    new_datetime: "",
    reason: "",
    meeting_link: "",
  });
const [confirmStatus, setConfirmStatus] = useState<string | null>(null);

  const rescheduleModalRef = useRef<any>(null);
  const hideBsModal = (id: string) => {
      const el = document.getElementById(id);
      if (!el) return;
      const bsModal = (window as any).bootstrap.Modal.getInstance(el) 
                  ?? (new (window as any).bootstrap.Modal(el));
      bsModal.hide();
      setTimeout(() => {
        document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
        document.body.classList.remove("modal-open");
        document.body.style.overflow = "";
      }, 300);
    };

const { hideModal } = useModalWithUrlCleanup(hideBsModal);

useEffect(() => {
  const el = document.getElementById("reschedule_modal");
  if (el) {
    rescheduleModalRef.current = new window.bootstrap.Modal(el, {
      backdrop: "static",
      keyboard: false,
    });
  }
}, []);
useEffect(() => {
  setRoundPage(1);
}, [detailData?.id]);
const allRounds = detailData?.rounds || [];

const totalRoundPages = Math.ceil(allRounds.length / ROUNDS_PAGE_SIZE);

const paginatedRounds = allRounds.slice(
  (roundPage - 1) * ROUNDS_PAGE_SIZE,
  roundPage * ROUNDS_PAGE_SIZE
);

const openRescheduleModal = (round: any) => {
  setRescheduleData({
    round_id: round.id,
    new_datetime: "",
    reason: "",
    meeting_link: round.meeting_link,
  });

  setRescheduleError({
    new_datetime: "",
    reason: "",
    meeting_link: "",
  });

  // Close rounds modal first
  hideModal("interview_rounds_modal");

  // Open reschedule modal after hiding the previous modal
  const roundsEl = document.getElementById("interview_rounds_modal");
  if (roundsEl) {
    roundsEl.addEventListener(
      "hidden.bs.modal",
      () => {
        rescheduleModalRef.current?.show();
      },
      { once: true }
    );
  }
};


  const fetchDesignation = async () => {
    try {
      const res = await nodeApi<any>(`/interviews/is-admin/${storedUser?.id}`);
      return res?.isAdmin || false;
    } catch (err) {
      console.error("Error fetching designation:", err);
      return "";
    }
  };

  useEffect(() => {
    const loadDesignation = async () => {
      const isAdmin = await fetchDesignation();
      setisAdmin(isAdmin);
    };

    if (storedUser?.designation_id) {
      loadDesignation();
    }
  }, [storedUser?.designation_id]);


const fetchInterviews = async () => {
  try {
    setLoading(true);

    const payload = {
      pageSize,
      currentPage,
      role: userRole,
      userId,
      searchTerm: debouncedSearchTerm || "",
     status: jobStatus === "ALL" ? null : jobStatus,
  jobProfile:
    jobProfile === "ALL"
      ? null
      : jobProfile
      ? Number(jobProfile)
      : null,
  // dateRange: [activeDateRange.from, activeDateRange.to],
    };
//     if (useDateFilter && activeDateRange.from && activeDateRange.to) {
//   payload.dateRange = [
//     activeDateRange.from,
//     activeDateRange.to
//   ];
// }

    const res = await nodeApi<any>(
      "/interviews/list-interviews",
      {
        method: "POST",
        body: payload,
      }
    );

    setInterviews(res?.data || []);
    setTotalItems(res?.pagination?.totalRecords || 0);

    
/*  JOB PROFILE OPTIONS */
if (res?.jobOpenings) {
  setJobProfileOptions((prev) => {
  const allOption = prev.find(o => o.value === "ALL") || {
    value: "ALL",
    label: "All Profiles",
  };

  return [
    allOption,
    ...res.jobOpenings.map((j: any) => ({
      value: String(j.id),
      label: j.job_profile,
    })),
  ];
});

}




  } catch (err: any) {
    console.error("Fetch error:", err);
  } finally {
    setLoading(false);
  }
};


useEffect(() => {
  fetchInterviews();
}, [
  currentPage,
  pageSize,
  debouncedSearchTerm,
  jobProfile,
  jobStatus,
  // activeDateRange.from,
  // activeDateRange.to,
]);


    useEffect(() => {
    if (!interviewIdFromURL) return;

    const loadInterview = async () => {
      try {
        const res:any = await nodeApi(
        `/interviews/interview`, {
            method: 'POST',
            body: { role: userRole, userId, id: interviewIdFromURL },
        }
      );
        if (res?.data) {
          setSelectedInterview(res.data);
          showBsModal("interview_rounds_modal");
        }
      } catch (err) {
        console.error("Error loading interview:", err);
      }
    };

    loadInterview();
  }, [interviewIdFromURL]);

    // const handleViewDetails = (candidate: any) => {
    //   setDetailData(candidate);   // directly set candidate object
    //   setDetailLoading(false);
    //   showBsModal("resume_details_modal");
    // };

   const handleViewDetails = (rec: any) => {
  setDetailData(rec);
  showBsModal("resume_details_modal");
};



    const handleOpenResume = (file_name: any) => {
      const url = file_name;
      if (!url) return toast.error("Resume link not found");
      window.open(url, "_blank", "noopener,noreferrer");
    };
    
    // --- Modal helpers (Bootstrap) ---
    const showBsModal = (id: string) => {
      const el = document.getElementById(id);
      if (!el) return;
      const bsModal = (window as any).bootstrap.Modal.getOrCreateInstance(el);
      bsModal.show();
    };

    const initialsOf = (name?: string) =>
    (name || "")
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase())
      .join("");

    const openInterviewModal = async (rec: any) => {
    try {
      const res: any = await nodeApi("/interviews/interview", {
        method: "POST",
        body: {
          role: userRole,
          userId,
          id: rec.id,
        },
      });

      if (res?.data) {
        setSelectedInterview(res.data);   //  ALWAYS FRESH
        setOpenReasonRoundIds({});        // reset view state
        setRoundStatusDraft({});          // reset draft state
        showBsModal("interview_rounds_modal");
      }
    } catch (e) {
      toast.error("Failed to load interview details");
    }
  };


const canShowFeedbackButtons = (round: any) => {
  const loggedInUserId = storedUser?.id;

  // Do not show buttons if round is Cancelled
  if (round?.status === "Cancelled") {
    return false;
  }

  return round?.interviewers?.some(
    (i: any) => Number(i?.interviewer_id) === Number(loggedInUserId)
  );
};



  // const approveRound = async (round: any) => {
  //   try {
  //     const res: any = await nodeApi("/interviews/approve-round", {
  //       method: "POST",
  //       body: {
  //         round_id: round.id,
  //         interviewer_id: storedUser.id
  //       }
  //     });

  //     toast.success(res?.message);
  //     fetchInterviews(); // reload list
  //   } catch (err: any) {
  //     toast.error(err.message);
  //   }
  // };

  const [rescheduleError, setRescheduleError] = useState({
  new_datetime: "",
  reason: "",
  meeting_link: "",
});

  const [isSaving, setIsSaving] = useState(false);

const submitReschedule = async () => {
  const errors: any = {};

  if (!rescheduleData.new_datetime) errors.new_datetime = "Date & time is required";
  if (!rescheduleData.reason.trim()) errors.reason = "Reason is required";
  if (!rescheduleData.meeting_link.trim()) errors.meeting_link = "Meeting link is required";

  setRescheduleError(errors);

  if (Object.keys(errors).length > 0) return;
  setIsSaving(true);

  try {
    const res:any = await nodeApi("/interviews/reschedule-round", {
      method: "POST",
      body: rescheduleData,
    });

    if (res.success) {
      toast.success("Interview rescheduled successfully");
      rescheduleModalRef.current?.hide();
      fetchInterviews();
    } else {
      toast.error(res.message || "Failed to reschedule");
    }
  }  catch (err: any) {
        const backendMessage = err?.response?.data?.message;
        console.error("Error:", backendMessage);
        toast.error(backendMessage || "API failed");
  } finally {
      setIsSaving(false);
    }
};


  const fetchInterviewers = async () => {
      try {
        const res = await nodeApi<any>(
          `/interviews/interviewers`
        );
        setInterviewers(res.data || []);
      } catch (err: any) {
        console.error("Fetch error:", err.message);
      }
    };

  const getLatestRoundReason = (
    round: any,
    interview: any,
    status?: "Cancelled" | "Not Shortlisted"
  ): string => {
    // ROUND-LEVEL reason (AUTHORITATIVE)
    if (round?.reason && round.reason.trim()) {
      return round.reason;
    }

    // Fallback: round communication_log
    let logs: any[] = [];

    if (Array.isArray(round?.communication_log)) {
      logs.push(...round.communication_log);
    } else if (typeof round?.communication_log === "string") {
      try {
        logs.push(...JSON.parse(round.communication_log));
      } catch {}
    }

    // LAST fallback: interview communication_log
    if (Array.isArray(interview?.communication_log)) {
      logs.push(...interview.communication_log);
    } else if (typeof interview?.communication_log === "string") {
      try {
        logs.push(...JSON.parse(interview.communication_log));
      } catch {}
    }

    const filtered = logs
      .filter(
        (l) =>
          l.type === "status_update" &&
          (!status || l.status === status) &&
          l.reason
      )
      .sort(
        (a, b) =>
          new Date(b.timestamp).getTime() -
          new Date(a.timestamp).getTime()
      );

    return filtered[0]?.reason || "";
  };
    useEffect(() => {
      fetchInterviewers();
    }, []);

    const getInterviewerName = (id: number): string => {
      const found = interviewers.find((iv) => iv.id === id);
      return found ? found.name : `Interviewer ${id}`;
    };

    useEffect(() => {
      if (selectedCandidate && selectedJob && interviewMode === "newRound") {
        console.log("DATA=",selectedCandidate)
        hideModal("interview_rounds_modal")
        // Open modal after hiding the previous modal
        const roundsEl = document.getElementById("interview_rounds_modal");
        if (roundsEl) {
          roundsEl.addEventListener(
            "hidden.bs.modal",
            () => {
              setShowInterviewModal(true);
            },
            { once: true }
          );
        }
      }
    }, [selectedCandidate, selectedJob, interviewMode]);

// useEffect(() => {
//   if (!selectedInterview?.rounds) return;

//   setRoundStatusDraft((prev) => {
//     const next = { ...prev };

//     selectedInterview.rounds.forEach((r) => {
//       if (
//         (r.status === "Cancelled" || r.status === "Not Shortlisted") &&
//         !next[r.id] //  IMPORTANT: do NOT overwrite
//       ) {
//         const reason = getLatestRoundReason(
//           r,
//           selectedInterview,
//           r.status as any
//         );

//         if (reason) {
//           next[r.id] = {
//             status: r.status,
//             reason,
//             error: "",
//           };
//         }
//       }
//     });

//     return next;
//   });
// }, [selectedInterview]);

  useEffect(() => {
    if (!selectedInterview?.id) return;

    // Reset all reason toggles when modal opens or interview reloads
    setOpenReasonRoundIds({});
  }, [selectedInterview?.id]);



   const scheduleNewRound = async (interview: any) => {
  try {
    const candidate = interview.candidate;
    const jobOpening = interview.jobOpening;
    setInterviewId(interview.id)
    setSelectedCandidate(candidate);
    setSelectedJob(jobOpening);
    setInterviewMode("newRound");
  } catch (err: any) {
    toast.error("Failed to load candidate details");
  }
};


const handleSuccess = () => {
    toast.success("New interview round scheduled successfully!");
    fetchInterviews();
};

const handleCloseModal = () => {
  setSelectedJob(null);
  setShowInterviewModal(false);
};

const handleConfirmSubmit = () => {
  if (!selectedRound) return;
  hideModal("interview_rounds_modal")
  submitFeedbackToHR(selectedRound);
};

const submitFeedbackToHR = async (round: any) => {
  try {
    const response: any = await nodeApi("/interviews/submit-feedback", {
      method: "POST",
      body: {
        round_id: round.id,
        interviewer_id: storedUser?.id
      }
    });

    if (response?.success) {
      toast.success("Feedback submission recorded & HR notified!");
      setShowConfirmPopup(false);
      fetchInterviews(); 
    } else {
      toast.error("API failed: Unable to submit feedback");
    }

  } catch (err: any) {
        const backendMessage = err?.response?.data?.message;
        console.error("Error:", backendMessage);
        toast.error(backendMessage || "API failed: Unable to submit feedback");
  } finally{
      setShowConfirmPopup(false)
  }
};

const updateStatus = async ({
  round_id = null,
  interview_id = null,
  status,
  reason = null
}: {
  round_id?: number | null;
  interview_id?: number | null;
  status: string;
  reason?: string | null;
}) => {
  try {
    if (!status) {
      toast.error("Status is required");
      return;
    }

    if (
      (!round_id && !interview_id) ||
      (round_id && interview_id)
    ) {
      toast.error("Send ONLY one of round_id OR interview_id");
      return;
    }

    const response: any = await nodeApi("/interviews/update-status", {
      method: "POST",
      body: {
        round_id,
        interview_id,
        status,
        reason
      },
    });

   if (response?.success) {
  toast.success(`Status updated to ${status}`);

  await fetchInterviews();

  if (round_id && selectedInterview?.id) {
    const refreshed = await nodeApi<{ data : InterviewRecord}>("/interviews/interview", {
      method: "POST",
      body: { role: userRole, userId, id: selectedInterview.id },
    });

   if (refreshed?.data) {
      setSelectedInterview(refreshed.data);

      // remove draft so saved reason can show
      setRoundStatusDraft((prev) => {
        const copy = { ...prev };
        if (round_id) delete copy[round_id];
        return copy;
      });

        // close any open reason panels
        setOpenReasonRoundIds({});
      }
      }
      // DO NOT close modal immediately
      setShowStatusModal(false);
      hideModal("interview_rounds_modal");
    } else {
      toast.error("Failed: Could not update status");
    }
  } catch (err: any) {
    const backendMessage = err?.response?.data?.message;
    console.error("Error:", backendMessage);
    toast.error(backendMessage || "API failed: Could not update status");
  }
};

const openStatusModal = (type: "round" | "interview", id: number) => {
  setStatusTarget({ type, id });
  setSelectedStatus(""); // reset selection
  setReason("")
  setReasonError("")
  setShowStatusModal(true);
};

const getLatestRound = (rounds: any[] = []): any => {
  if (!Array.isArray(rounds) || rounds.length === 0) return null;

  return rounds
    .filter(r => r?.scheduled_datetime)
    .sort(
      (a, b) =>
        new Date(b.scheduled_datetime).getTime() -
        new Date(a.scheduled_datetime).getTime()
    )[0];
};

  const columns = [
    {
  title: "Name",
  dataIndex: "name",
  width: 180,
  render: (_: any, rec: any) => {
    const maxNameLength = 15;
    const maxEmailLength = 20;

    const truncate = (text: string, max: number) =>
      text?.length > max ? text.substring(0, max) + "..." : text;

    return (
      <div style={{ minWidth: 0 }}>
        <a
          href="#"
          className="fw-medium text-decoration-none"
          onClick={(e) => {
            e.preventDefault();

            if (userRole === 2) {
              // if (rec.candidate?.file_name) {
              //   window.open(rec.candidate.file_name, "_blank");
              // } else {
              //   alert("Resume is not uploaded.");
              // }
              handleViewDetails(rec);
            } else {
              handleViewDetails(rec);
            }
          }}
          title={userRole === 2 ? "Open candidate details" : "Open candidate details"}
        >
          {truncate(rec.candidate.name, maxNameLength)}
        </a>

        <div className="text-muted small" style={{marginTop:"-4px"}}>
          {rec.candidate.email
            ? truncate(rec.candidate.email, maxEmailLength)
            : "—"}
        </div>
      </div>
    );
  },
},
  {
    title: "Job Profile",
    dataIndex: "jobOpening",
    width: 180,
    ellipsis: true,
    render: (_: any, rec: any) => {
      const value = rec?.jobOpening?.job_profile || "-";
      return <span title={value}>{value}</span>;
    }
  },
{
  title: "Status",
  dataIndex: "record",
  width: 180,
  ellipsis: true,
  align: "center",
render: (_: any, record: any) => {
  const status = record.status;

    // Normalize communication_log to an array
  let logs: any[] = [];
    if (Array.isArray(record.communication_log)) {
      logs = record.communication_log;
    } else if (typeof record.communication_log === "string") {
      try {
      logs = JSON.parse(record.communication_log);
  } catch {
    logs = [];
  }
    }

    // Filter only status_update logs with Not Shortlisted
    const statusLogs = logs.filter(
      (log: any) => log.type === "status_update" && (log.status === "Not Shortlisted" || log.status === "Cancelled")
    );

    // Get latest log by timestamp
    const latestLog = statusLogs.sort(
      (a: any, b: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    )[0];

  return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
      <span>{status || "-"}</span>

        {(status === "Not Shortlisted" || status === "Cancelled") && latestLog?.reason && (
          <i
            className="ti ti-info-circle ms-1 text-muted"
            style={{ cursor: "pointer" }}
            data-bs-toggle="tooltip"
            data-bs-placement="bottom"
            data-bs-html="false"
            title={latestLog.reason}
          />
      )}
    </div>
  );
  },
},
   {
    title: "Progress Status",
    dataIndex: "rounds",
    width: 200,
    ellipsis: true,
    render: (_: any, rec: any) => {
      const latest = getLatestRound(rec.rounds);

      if (!latest) return "-";

      return (
         <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <span className="fw-medium">{latest.interview_type}</span>
          <span className="text-muted"> - </span>
          <span>{latest.status || "-"}</span>
        </div>
      );
    }
  },
  {
    title: "Scheduled On",
    dataIndex: "scheduled",
    width: 180,
    ellipsis: true,
    render: (_: any, rec: any) => {
      const latest = getLatestRound(rec.rounds);

      if (!latest?.scheduled_datetime) return "-";

      return dayjs(latest.scheduled_datetime).format("DD-MMM-YYYY, h:mm A");
    }
  },
  {
    title: "Action",
    dataIndex: "actions",
    width: 80,
    align: "center",
    render: (_: any, rec: any) => (
      <div className="action-icon d-inline-flex">
        <Link
          to="#"
          title="View Round Details"
          onClick={(e) => {
            e.preventDefault();
            openInterviewModal(rec);
          }}
        >
          <i className="ti ti-eye" />
        </Link>

        {userRole === 1  && (
          <Link
          to="#"
          title="Change Status"
          onClick={(e) => {
            e.preventDefault();
            openStatusModal("interview", rec.id)
          }}
        >
          <i className="ti ti-edit" />
        </Link>
        )}
      </div>
    ),
  }
  ];
// const isDefaultDate =
//   activeDateRange.from === DEFAULT_START_ISO &&
//   activeDateRange.to === DEFAULT_END_ISO;

const showReset =
  searchTerm.trim() !== "" ||
  jobProfile !== "ALL" ||
  jobStatus !== "ALL" 
  // !isDefaultDate;

const handleResetFilters = () => {
  setSearchTerm("");
  setJobProfile("ALL");
  setJobStatus("ALL");

  // setActiveDateRange({
  //   from: DEFAULT_START_ISO,
  //   to: DEFAULT_END_ISO,
  // });

  // setSelectedStartLabel(dayjs(DEFAULT_START_ISO).format("DD-MMM-YYYY"));
  // setSelectedEndLabel(dayjs(DEFAULT_END_ISO).format("DD-MMM-YYYY"));

  // setUseDateFilter(true);
  setCurrentPage(1);

  setShowMobileFilters(false); 
  navigate("/interviews", { replace: true });
};



const headerFiltersNode = useMemo(() => {
  // const showReset = searchTerm.trim() !== "";

  return (
    <div className="upper-absolute-class">

      {/* ================= MOBILE ================= */}
      <div className="d-lg-none">
        <div className="d-flex align-items-center gap-2" style={{ paddingRight: 12 }}>
          
         {showReset &&(
            <button
              className="btn btn-outline-primary add_btn_size"
              onClick={handleResetFilters}
            >
              <i className="ti ti-refresh" />
            </button>
          )}

<div className="d-flex w-100 gap-2 ">


          {/* Search – 50% */}
         
            <input
              type="text"
              className="form-control search-size w-45"
              placeholder="Search…"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
           </div>

          {/* Date Picker – 50% */}
          {/* <div
 
              className="input-icon-end position-relative custom-datepicker holiday-flex w-100 datepicker-anchor preset-popover"
                onClick={handleActivateDateFilterOnClick}
              >
                <div
                className="datepicker-shell "
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  minHeight: 38,
                  height: "100%",          
                  borderRadius: 4,
                  width: "100%",
                }}
              >

              <PredefinedDateRanges
                key={`${selectedStartLabel}-${selectedEndLabel}`}
                startDate={dayjs(selectedStartLabel, "DD-MMM-YYYY", true).format("YYYY-MM-DD")}
                endDate={dayjs(selectedEndLabel, "DD-MMM-YYYY", true).format("YYYY-MM-DD")}
                onDateRangeChange={(isoStart, isoEnd) =>
                  handleDateRangeChangeFilter(
                    dayjs(isoStart).format("DD-MMM-YYYY"),
                    dayjs(isoEnd).format("DD-MMM-YYYY")
                  )
                }
              />
            </div>

            <span className="input-icon-addon">
              <i className="ti ti-chevron-down" />
            </span>
          </div> */}

          <button
            className="btn btn-outline-primary add_btn_size"
            onClick={() => setShowMobileFilters(true)}
          >
            <i className="ti ti-filter" />
          </button>
        </div>

        {/* Mobile filter modal */}
         {showMobileFilters && (
          <div
            className="modal-backdrop"
            onClick={() => setShowMobileFilters(false)} // CLOSE ON OUTSIDE CLICK
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.3)",
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
              paddingTop: "103px",
              zIndex: 1050,
            }}
          >
              <div
                ref={modalRef}
                className="bg-white shadow rounded p-3 mt-2 filter_select"
                style={{ width: "100%", maxWidth: 400 }}
                onClick={(e) => e.stopPropagation()} //  PREVENT CLOSE INSIDE
              >

              {/* <CommonSelect
                className="select header-filter-select"
                forceFullWidth
                options={STATUS_OPTIONS}
                placeholder="Status"
                onChange={() => setShowMobileFilters(false)}
              /> */}
             <div className="d-flex flex-column gap-2">
                <CommonSelect
                  placeholder="Job Profile"
                  className="select header-filter-select"
                  options={jobProfileOptions}
                  forceFullWidth
                  value={
                jobProfileOptions.find(o => o.value === jobProfile) ||
                jobProfileOptions[0]
              }

              onChange={(opt) => {
                setJobProfile(opt?.value ?? "ALL");
                setCurrentPage(1);
                setShowMobileFilters(false);
              }}
            />

              <CommonSelect
                placeholder="Job Status"
                className="select header-filter-select"
                options={STATUS_OPTIONS}
                forceFullWidth
                value={STATUS_OPTIONS.find(o => o.value === jobStatus) || STATUS_OPTIONS[0]}
                onChange={(opt) => {
                  setJobStatus(opt?.value ?? "ALL");
                  setCurrentPage(1);
                  setShowMobileFilters(false);
                }}
              />
            </div>



            </div>
          </div>
        )} 
      </div>

      {/* ================= DESKTOP ================= */}
      <div className="Employee-filters d-none d-lg-flex align-items-center flex-wrap gap-2">

       {showReset &&(
          <button
            className="btn btn-outline-primary add_btn_size"
             onClick={handleResetFilters}
          >
            <i className="ti ti-refresh" />
          </button>
        )}

        <input
          type="text"
          className="form-control search-size"
          placeholder="Search…"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
        />

        {/* <CommonSelect
          className="select header-filter-select"
          options={STATUS_OPTIONS}
          placeholder="Status"
        /> */}
          <CommonSelect
            placeholder="Job Profile"
            className="select header-filter-select"
            options={jobProfileOptions}
          value={
            jobProfileOptions.find(o => o.value === jobProfile) ||
            jobProfileOptions[0]
          }

            onChange={(opt) => {
              setJobProfile(opt?.value ?? "ALL");
              setCurrentPage(1);
            }}
          />



              <CommonSelect
                placeholder="Job Status"
                className="select header-filter-select"
                options={STATUS_OPTIONS}
                value={STATUS_OPTIONS.find(o => o.value === jobStatus) || STATUS_OPTIONS[0]}
                onChange={(opt) => {
                  setJobStatus(opt?.value ?? "ALL");
                  setCurrentPage(1);
                }}
              />


              {/* <div
                 className="input-icon-end position-relative custom-datepicker me-1"
                onClick={handleActivateDateFilterOnClick}
              >
                <PredefinedDateRanges
                  startDate={activeDateRange.from}
                  endDate={activeDateRange.to}
                  onDateRangeChange={(isoStart: string, isoEnd: string) => {
                    setUseDateFilter(true);
                    setActiveDateRange({ from: isoStart, to: isoEnd });
                    setSelectedStartLabel(dayjs(isoStart).format("DD-MMM-YYYY"));
                    setSelectedEndLabel(dayjs(isoEnd).format("DD-MMM-YYYY"));
                    setCurrentPage(1);
                  }}
                />
                <span className="input-icon-addon">
                  <i className="ti ti-chevron-down" />
                </span>
              </div> */}


      </div>
    </div>
  );
}, [searchTerm, showMobileFilters,jobProfile, jobStatus,  jobProfileOptions]);

useEffect(() => {
  setFilters(headerFiltersNode);
  return () => setFilters(null);
}, [headerFiltersNode]);

useEffect(() => {
  if (!encryptedQuery || hasHydratedFromUrl.current) return;

  try {
    isSyncingFromUrl.current = true;

    const decoded = decodeURIComponent(encryptedQuery);
    const result = decryptQuery(decoded) as InterviewFilters;

    if (result?.searchTerm !== undefined) {
      setSearchTerm(result.searchTerm || "");
    }

    if (result?.currentPage) {
      setCurrentPage(result.currentPage);
    }
    if (result?.jobProfile !== undefined) {
  setJobProfile(result.jobProfile ?? "ALL");
}

    if (result?.jobStatus) setJobStatus(result.jobStatus);

//     if (result?.dateRange) {
//   const [from, to] = result.dateRange;

//   setActiveDateRange({ from, to });
//   setSelectedStartLabel(dayjs(from).format("DD-MMM-YYYY"));
//   setSelectedEndLabel(dayjs(to).format("DD-MMM-YYYY"));
//   setUseDateFilter(true);
// }


    hasHydratedFromUrl.current = true;
  } catch (e) {
    console.warn("Failed to decrypt interview filters");
  } finally {
    requestAnimationFrame(() => {
      isSyncingFromUrl.current = false;
    });
  }
}, [encryptedQuery]);
useEffect(() => {
  if (isSyncingFromUrl.current) return;

  const filters: InterviewFilters = {
    searchTerm: debouncedSearchTerm || null,
    currentPage,
    jobProfile,
    jobStatus,
    // dateRange: [activeDateRange.from, activeDateRange.to],
  };

 const isDefault =
    !filters.searchTerm &&
   (filters.jobProfile === "ALL" || !filters.jobProfile) &&
  (filters.jobStatus === "ALL" || !filters.jobStatus) &&
    currentPage === 1 
    // &&
    // filters.dateRange?.[0] === DEFAULT_START_ISO &&
    // filters.dateRange?.[1] === DEFAULT_END_ISO;

  if (isDefault) {
    navigate("/interviews", { replace: true }); 
    return;
  } else {
    const encrypted = encryptQuery(filters);
    navigate(`/interviews/${encodeURIComponent(encrypted)}`, {
      replace: false,
    });
  }
}, [debouncedSearchTerm, currentPage, jobProfile, jobStatus]);

  return (
    <>
    <div className="page-wrapper d-flex flex-column">
      <div className="content flex-grow-1 " >
        <div className="card card-margin-bottom" ><div className="card-body p-0" id="payheader-list" style={{ height: tableHeight, display: "flex", flexDirection: "column" }}>
        <Table  style={{ flex: 1 }} loading={loading} currentPage={currentPage} dataSource={interviews} columns={columns} onPageChange={setCurrentPage} totalItems={totalItems} pageSize={pageSize} />
      </div></div></div>

      {/* Details Modal */}
      <div className="modal fade" id="resume_details_modal" tabIndex={-1}>
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                      <div className="modal-content border-0 shadow-lg">
                        <div className="modal-header border-0 pb-0">
                          <div className="d-flex align-items-center gap-3">
                            {/* Professional avatar with initials */}
                            <div
                              className="d-flex align-items-center justify-content-center text-white"
                              style={{
                                width: 56,
                                height: 56,
                                borderRadius: "14px",
                                background:
                                  "linear-gradient(135deg, #3b82f6 0%, #0ea5e9 100%)",
                                fontSize: 20,
                                fontWeight: 600,
                              }}
                              aria-hidden
                            >
                              {initialsOf(detailData?.candidate?.name) || "CN"}
                            </div>
      
                            <div>
                              <h5 className="modal-title mb-0">{detailData?.candidate?.name || "Candidate"}</h5>
                              <div className="text-muted small">
                                {detailData?.candidate?.designation || "—"} · {detailData?.candidate?.current_location || "—"}
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="btn-close"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                            onClick={() => hideBsModal("resume_details_modal")}
                          />
                        </div>
      
                        <div className="modal-body pt-3">
                          {/* Quick actions */}
                          <div className="d-flex flex-wrap gap-2 mb-3">
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handleOpenResume(detailData?.candidate?.file_name)}
                            >
                              <i className="ti ti-file-text me-1" /> Open Resume
                            </button>
                          </div>
      
                          {/* Key facts */}
                          {detailLoading && <div>Loading...</div>}
                          {!detailLoading && (
                            <div className="row g-3">
                              <div className="col-12 col-md-6">
                                <div className="card h-100 border-0 shadow-sm">
                                  <div className="card-body">
                                    <h6 className="text-uppercase text-muted mb-3">Profile</h6>
                                    <dl className="row mb-0 small">
                                      <dt className="col-5 text-muted">Email</dt>
                                        <dd className="col-7">
                                          {detailData?.candidate?.email ? (
                                            <span
                                              className="text-primary"
                                              style={{ cursor: "pointer" }}
                                            >
                                              {detailData.candidate.email}
                                            </span>
                                          ) : (
                                            "—"
                                          )}
                                        </dd>
                                        <dt className="col-5 text-muted">Contact</dt>
                                        <dd className="col-7">
                                          {detailData?.candidate?.contact_no ? (
                                            <span
                                              className="text-primary"
                                              style={{ cursor: "pointer" }}
                                            >
                                              {detailData.candidate.contact_no}
                                            </span>
                                          ) : (
                                            "—"
                                          )}
                                        </dd>
                                      <dt className="col-5 text-muted">Experience</dt>
                                      <dd className="col-7">
                                        {(() => {
                                          const n = Number(detailData?.candidate?.total_experience_months) || 0;
                                          const y = Math.floor(n / 12);
                                          const r = n % 12;
                                          if (y && r) return `${y} year${y > 1 ? "s" : ""} ${r} month${r > 1 ? "s" : ""}`;
                                          if (y) return `${y} year${y > 1 ? "s" : ""}`;
                                          return `${r} month${r !== 1 ? "s" : ""}`;
                                        })()}
                                      </dd>
      
                                      <dt className="col-5 text-muted">Source</dt>
                                      <dd className="col-7">{detailData?.candidate?.source || "—"}</dd>
      
                                    {userRole === 1 && (
                                      <>
                                        <dt className="col-5 text-muted">CTC</dt>
                                        <dd className="col-7">{detailData?.candidate?.ctc_info || "—"}</dd>
                                      </>
                                    )}
                                    </dl>
                                  </div>
                                </div>
                              </div>
      
                              <div className="col-12 col-md-6">
                                <div className="card h-100 border-0 shadow-sm">
                                  <div className="card-body">
                                    <h6 className="text-uppercase text-muted mb-3">Career</h6>
                                    <dl className="row mb-0 small">
                                      <dt className="col-5 text-muted">Companies Worked</dt>
                                      <dd className="col-7">{detailData?.candidate?.companies_worked ?? "—"}</dd>
      
                                      <dt className="col-5 text-muted">Last Company</dt>
                                      <dd className="col-7">{detailData?.candidate?.last_company || "—"}</dd>
      
                                      <dt className="col-5 text-muted">Category</dt>
                                      <dd className="col-7">{detailData?.candidate?.category_name || "—"}</dd>
      
                                      <dt className="col-5 text-muted">Loyalty</dt>
                                      <dd className="col-7">
                                        {detailData?.candidate?.loyalty_percentage != null ? `${detailData.candidate.loyalty_percentage}%` : "—"}
                                      </dd>
                                    </dl>
                                  </div>
                                </div>
                              </div>
      
                              {/* Skills */}
                              <div className="col-12">
                                <div className="card border-0 shadow-sm">
                                  <div className="card-body">
                                    <h6 className="text-uppercase text-muted mb-3">Skills</h6>
                                    <div className="d-flex flex-wrap gap-2">
                                      {(Array.isArray(detailData?.candidate?.skills)
                                        ? detailData.candidate.skills
                                        : detailData?.candidate?.skills
                                        ? String(detailData.candidate.skills).split(",")
                                        : []
                                      ).map((s: string, i: number) => (
                                        <span key={i} className="badge bg-light text-dark border">
                                          {s.trim()}
                                        </span>
                                      ))}
                                      {(!detailData?.candidate?.skills ||
                                        (Array.isArray(detailData.candidate.skills) && detailData.candidate.skills.length === 0)) && (
                                        <span className="text-muted small">—</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Screening Notes */}
                              <div className="col-12 mt-0  pt-0">
                                <div className="card border-0 shadow-sm mt-0">
                                  <div className="card-body">
                                    <h6 className="text-uppercase text-muted mb-3">
                                      Screening Notes
                                    </h6>

                                    <div className="small text-dark" style={{ whiteSpace: "pre-wrap" }}>
                                      {detailData?.description?.trim()
                                        ? detailData.description
                                        : "—"}
                                    </div>
                                  </div>
                                </div>
                              </div>

                             {/* Interview Rounds Card */}
                              <div className="col-12 mt-0  pt-0">
                                <div className="card border-0 shadow-sm">
                                  <div className="card-body">

                                    <h6 className="text-uppercase text-muted mb-3">
                                      Interview Rounds
                                    </h6>

                                    {detailData?.rounds?.length ? (
                                      <div className="table-responsive">
                                        <table className="table table-sm align-middle mb-0">
                                          <thead className="table-light small">
                                            <tr>
                                              <th>Date</th>
                                              <th>Time</th>
                                              <th>Round</th>
                                              <th>Status</th>
                                              <th>Interviewers</th>
                                              
                                            </tr>
                                          </thead>

                                          <tbody className="small">
                                            {paginatedRounds.map((round: any, index: number) => (
                                              <tr key={round.id}>

                                                 {/* Date */}
                                               <td>
                                                  {round.scheduled_datetime
                                                    ? new Date(round.scheduled_datetime)
                                                        .toLocaleDateString("en-GB", {
                                                          day: "2-digit",
                                                          month: "short",
                                                          year: "numeric",
                                                        })
                                                        .replace(/ /g, "-")
                                                    : "—"}
                                                </td>
                                                <td>
                                                    {round.scheduled_datetime
                                                      ? new Date(round.scheduled_datetime).toLocaleTimeString("en-US", {
                                                          hour: "2-digit",
                                                          minute: "2-digit",
                                                          hour12: true,
                                                        })
                                                      : "—"}
                                                  </td>

                                                
                                                {/* Round Name / Type */}
                                                <td className="fw-semibold">
                                                  {round.interview_type || `Round ${index + 1}`}
                                                </td>

                                                {/* Status */}
                                                <td>
                                                  <span
                                                    className={`badge ${
                                                      round.status === "Scheduled"
                                                        ? "bg-warning-subtle text-warning"
                                                        : round.status === "Completed"
                                                        ? "bg-success-subtle text-success"
                                                        : round.status === "Cancelled"
                                                        ? "bg-danger-subtle text-danger"
                                                        : round.status === "Rescheduled"
                                                        ? "bg-info-subtle text-info"
                                                        : "bg-secondary-subtle text-secondary"
                                                    }`}
                                                  >
                                                    {round.status}
                                                  </span>
                                                </td>

                                                {/* Interviewers */}
                                                <td>
                                                  {round.interviewers?.length
                                                    ? round.interviewers
                                                        .map((i: any) => i.interviewer?.name)
                                                        .join(", ")
                                                    : "—"}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    ) : (
                                      <div className="text-muted small">
                                        No interview rounds found
                                      </div>
                                    )}
                                    {totalRoundPages > 1 && (
                                      <div className="d-flex justify-content-between align-items-center mt-3">
                                        <small className="text-muted">
                                          Page {roundPage} of {totalRoundPages}
                                        </small>

                                        <div className="btn-group btn-group-sm">
                                          <button
                                            className="btn btn-outline-secondary"
                                            disabled={roundPage === 1}
                                            onClick={() => setRoundPage((p) => p - 1)}
                                          >
                                            Prev
                                          </button>

                                          <button
                                            className="btn btn-outline-secondary"
                                            disabled={roundPage === totalRoundPages}
                                            onClick={() => setRoundPage((p) => p + 1)}
                                          >
                                            Next
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                            </div>
                          )}
                        </div>
      
                        <div className="modal-footer border-0">
                          <button className="btn btn-light" onClick={() => hideBsModal("resume_details_modal")}>
                            Close
                          </button>
                        </div>
                      </div>
                    </div>
      </div>

{/* Interview Rounds Modal */}
      <div
  className="modal fade"
  id="interview_rounds_modal"
  tabIndex={-1}
  aria-hidden="true"
>
  <div className="modal-dialog modal-lg modal-dialog-scrollable">
    <div className="modal-content">
       <div className="modal-header d-flex align-items-start justify-content-between flex-wrap">
    <h4
      className="modal-title d-flex flex-wrap align-items-center gap-2 rounds-header"
      style={{ margin: 0 }}
    >
      <span>Interview Rounds |</span>

      {selectedInterview?.candidate?.name && (
        <span
          className="text-muted small text-truncate candidate-name"
          title={selectedInterview.candidate.name}
        >
          {selectedInterview.candidate.name}
        </span>
      )}
    </h4>

    {/* Close button */}
    <button
      type="button"
      className="btn-close"
      data-bs-dismiss="modal"
      aria-label="Close"
    />
  </div>

      <div className="modal-body">
  {!selectedInterview ? (
    <p>Loading...</p>
  ) : (
    <div>
      {selectedInterview.rounds?.length === 0 && <p>No rounds found.</p>}

      {selectedInterview?.rounds?.map((r: InterviewRound) => {
        let latestReschedule: any = null;
        const canShowActions = r.feedback_summary === null;
        if (r.reschedule_history) {
          try {
            let history = r.reschedule_history;
            if (typeof history === "string") history = JSON.parse(history);
            if (Array.isArray(history) && history.length > 0) {
              latestReschedule = history[history.length - 1];
            }
          } catch (e) {
            console.error("Invalid reschedule_history format:", e);
          }
        }

        return (
          <div key={r.id} className="p-3 mb-3 border rounded shadow-sm">
            {/* Top row: title + HR actions */}
            <div className="d-flex justify-content-between align-items-start mb-2">
             <h5 className="fw-semibold mb-0 d-flex align-items-center gap-2">
              <span>{r.interview_type} Round</span>
                {(r.status === "Cancelled" || r.status === "Not Shortlisted") && (
                  <Popover
                    content={openReasonRoundIds[r.id] ? "Hide reason" : "View reason"}
                    trigger="hover"
                    placement="right"
                    getPopupContainer={() =>
                      document.getElementById("interview_rounds_modal") as HTMLElement
                    }
                    overlayStyle={{ zIndex: 2000 }}
                  >
                    <span
                      style={{
                        cursor: "pointer",
                        color: "#6b7280",
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenReasonRoundIds((prev) => ({
                          ...prev,
                          [r.id]: !prev[r.id],
                        }));
                      }}
                    >
                      <InfoCircleOutlined />
                    </span>
                  </Popover>
                )}
            </h5>


              {/* HR Change Status */}
             {userRole === 1 && (
              <CommonSelect
                className="select"
                options={ROUND_STATUS_OPTIONS}
                placeholder="Change Status"
                value={
                    roundStatusDraft[r.id]
                      ? {
                          value: roundStatusDraft[r.id].status,
                          label: roundStatusDraft[r.id].status,
                        }
                      : r.status
                      ? { value: r.status, label: r.status }
                      : null
                  }

                onChange={(opt) => {
                if (!opt) return;

                // CASE 1: Switching AWAY from draft → cleanup immediately
                if (
                  roundStatusDraft[r.id] &&
                  opt.value !== "Cancelled" &&
                  opt.value !== "Not Shortlisted"
                ) {
                  setRoundStatusDraft((prev) => {
                    const copy = { ...prev };
                    delete copy[r.id];
                    return copy;
                  });

                  // call API directly
                  updateStatus({
                    round_id: r.id,
                    status: opt.value,
                  });
                  return;
                }

                //  CASE 2: Reason-required status
                if (opt.value === "Cancelled" || opt.value === "Not Shortlisted") {
                  setRoundStatusDraft((prev) => ({
                    ...prev,
                    [r.id]: {
                      status: opt.value,
                      reason: "",
                      error: "",
                    },
                  }));
                  return;
                }

                if (opt.value === r.status && !roundStatusDraft[r.id]) return;

                // CASE 3: Normal status (no draft exists)
                updateStatus({
                  round_id: r.id,
                  status: opt.value,
                });
              }}
              />
            )}
            </div>

            {/* Round details */}
            <p className="mb-1">
              <strong>Scheduled On:</strong>{" "}
              {r.scheduled_datetime
                ? dayjs(r.scheduled_datetime).format("DD-MMM-YYYY, h:mm A")
                : "-"}
            </p>

            <p className="mb-1">
              <strong>Status:</strong> {r.status}
            </p>

            {/* Reason inline box in interview round modal */}
            {roundStatusDraft[r.id] &&
              (roundStatusDraft[r.id].status === "Cancelled" ||
                roundStatusDraft[r.id].status === "Not Shortlisted") && (
                <div className="mt-3 border rounded p-3 bg-light">

                  {/* Header row */}
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label mb-0">
                      Reason <span className="text-danger">*</span>
                    </label>

                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => {
                          const draft = roundStatusDraft[r.id];

                          if (!draft.reason.trim()) {
                            setRoundStatusDraft((prev) => ({
                              ...prev,
                              [r.id]: {
                                ...draft,
                                error: "Reason is required",
                              },
                            }));
                            return;
                          }

                          updateStatus({
                            round_id: r.id,
                            status: draft.status,
                            reason: draft.reason,
                          });

                          // setRoundStatusDraft((prev) => {
                          //   const copy = { ...prev };
                          //   delete copy[r.id];
                          //   return copy;
                          // });
                        }}
                      >
                        Save
                      </button>

                    <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => {
                          //  remove draft
                          setRoundStatusDraft(prev => {
                            const copy = { ...prev };
                            delete copy[r.id];
                            return copy;
                          });

                          // force-close info panel to avoid stale view
                          setOpenReasonRoundIds(prev => ({
                            ...prev,
                            [r.id]: false,
                          }));
                        }}
                      >
                        Cancel
                      </button>

                    </div>
                  </div>

                  {/* Textarea */}
                  <TextareaAutosize
                    className={`form-control ${
                      roundStatusDraft[r.id].error ? "is-invalid" : ""
                    }`}
                    minRows={2}
                    maxRows={4}
                    placeholder="Enter reason for status change"
                    value={roundStatusDraft[r.id].reason}
                    onChange={(e) => {
                      const val = e.target.value;
                      setRoundStatusDraft((prev) => ({
                        ...prev,
                        [r.id]: {
                          ...prev[r.id],
                          reason: val,
                          error: "",
                        },
                      }));
                    }}
                  />

                  {roundStatusDraft[r.id].error && (
                    <div className="invalid-feedback d-block">
                      {roundStatusDraft[r.id].error}
                    </div>
                  )}
                </div>
            )}
            {/* SHOW SAVED REASON WHEN NO DRAFT */}
            {openReasonRoundIds[r.id] &&
              !roundStatusDraft[r.id] &&
              (r.status === "Cancelled" || r.status === "Not Shortlisted") && (
                (() => {
                  const savedReason = getLatestRoundReason(
                    r,
                    selectedInterview,
                    r.status as any
                  );
                  if (!savedReason) return null;

                  return (
                    <div className="mt-0 border rounded p-3 bg-light">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="form-label mb-0 fw-semibold">Reason</label>

                        {userRole === 1 && (
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => {
                              // close view-mode panel
                              setOpenReasonRoundIds(prev => ({
                                ...prev,
                                [r.id]: false,
                              }));

                              // open draft
                              setRoundStatusDraft(prev => ({
                                ...prev,
                                [r.id]: {
                                  status: r.status,
                                  reason: savedReason,
                                  error: "",
                                },
                              }));
                            }}

                          >
                            Edit
                          </button>
                        )}
                      </div>

                      <div className="small text-dark" style={{ whiteSpace: "pre-wrap" }}>
                        {savedReason}
                      </div>
                    </div>
                  );
                })()
            )}

            {r.status === "Rescheduled" && latestReschedule && (
              <p className="mb-1">
                <strong>Reason:</strong> {latestReschedule.reason}
              </p>
            )}

            <p className="mb-2">
              <strong>Interviewers:</strong>{" "}
              {r.interviewers
                .map((i: Interviewer) => getInterviewerName(i.interviewer_id))
                .join(", ")}
            </p>

            {/* Action buttons */}
           <div className="d-flex gap-2 mt-3 flex-wrap">
            {canShowActions ? (
              <>
                <button
                  className="btn btn-sm"
                  style={{
                    backgroundColor: "#AFC2FF",
                    color: "#3F4D92",
                                border: "none" // optional, removes default btn border
                  }}
                  onClick={() => window.open(r.meeting_link, "_blank")}
                  disabled={!r.meeting_link}
                >
                  Join Meeting
                </button>

                {canShowFeedbackButtons(r) && (
                  <button
                    className="btn btn-sm"
                    style={{
                      backgroundColor: "#CD8B015E",
                      color: "#A87C20",
                      border: "none",
                    }}
                    onClick={() => window.open(r.feedback_form_link, "_blank")}
                    disabled={!r.feedback_form_link}
                  >
                    Give Feedback
                  </button>
                )}

                {canShowFeedbackButtons(r) && (
                  <button
                    className="btn btn-sm"
                    style={{
                      backgroundColor: "#9FF4C4",
                      color: "#34714E",
                      border: "none",
                    }}
                    onClick={() => {
                      setSelectedRound(r);
                      setShowConfirmPopup(true);
                    }}
                  >
                    I Have Submitted Feedback
                  </button>
                )}

              {userRole === 1  && (
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => openRescheduleModal(r)}
                  >
                    Reschedule
                  </button>
                )}
              </>
            ) : (
              <button
                className="btn btn-sm"
                disabled
                style={{
                  backgroundColor: "#E5E7EB", 
                  color: "#6B7280",           
                  border: "none",
                  cursor: "not-allowed",
                }}
                title="Feedback already submitted"
              >
                Feedback Submitted
              </button>
            )}
          </div>
          </div>
        );
      })}
    </div>
  )}
      </div>

      <div className="modal-footer">
         {userRole === 1 && (
          <button
            className="btn me-auto"
            style={{
              backgroundColor: "#9FF4C4",
              color: "#34714E",
              border: "none"
            }}
            onClick={() => {
              if (selectedInterview?.jobOpening?.status === "inactive") {
                toast.error("This job opening is inactive. You cannot schedule a new round.");
                return;
              }
              scheduleNewRound(selectedInterview);
            }}
          >
            Schedule New Round
          </button>
        )}
        <button
          className="btn btn-light"
          onClick={() => hideModal("interview_rounds_modal")}
        >
          Close
        </button>
      </div>
    </div>
  </div>
</div>

{showStatusModal && (
  <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
    <div className="modal-dialog modal-dialog-centered">
      <div className="modal-content">

        <div className="modal-header">
          <h5 className="modal-title">Change Status</h5>
          <button className="btn-close" onClick={() => setShowStatusModal(false)}></button>
        </div>

        <div className="modal-body">

          <div className="d-flex flex-column gap-2">
            {getStatusOptions().map((s) => (
              <button
                key={s}
                className={`btn ${selectedStatus === s ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setSelectedStatus(s)}
              >
                {s}
              </button>
            ))}
          </div>

          {/* Reason only for interviews with Not Shortlisted */}
          {statusTarget.type === "interview" && (selectedStatus === "Not Shortlisted" || selectedStatus === "Cancelled") && (
          <div className="mt-3">
            <label className="form-label">
              Reason <span className="text-danger">*</span>
            </label>

            <TextareaAutosize
              className={`form-control`}
              minRows={2}
              maxRows={4}
              value={reason}
              onChange={(e) => {
                const val = e.target.value;
                setReason(val);
                if (val.trim() !== "") {
                  setReasonError("");
                }
              }}
            />
            {reasonError && <div className="text-danger">{reasonError}</div>}
          </div>
        )}
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-light me-2"
            onClick={() => setShowStatusModal(false)}
          >
            Cancel
          </button>

          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={
              !selectedStatus
            }
          >
            Save
          </button>
        </div>

      </div>
    </div>
  </div>
)}

{showConfirmPopup && (
  <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
    <div className="modal-dialog modal-dialog-centered">
      <div className="modal-content">

        <div className="modal-header">
          <h5 className="modal-title">Confirm Feedback Submission</h5>
          <button className="btn-close" onClick={() => setShowConfirmPopup(false)}></button>
        </div>

        <div className="modal-body">
          <p>Are you sure you have submitted the feedback form?</p>
        </div>

        <div className="modal-footer">
          <button className="btn btn-light me-2" onClick={() => setShowConfirmPopup(false)}>
            Cancel
          </button>

          <button
            className="btn btn-primary"
            onClick={() => handleConfirmSubmit()}
          >
            Yes, Submit
          </button>
        </div>

      </div>
    </div>
  </div>
)}

    <div
  className="modal fade"
  id="reschedule_modal"
  tabIndex={-1}
  aria-hidden="true"
>
  <div className="modal-dialog">
    <div className="modal-content">

      <div className="modal-header">
        <h4 className="modal-title">Reschedule Interview</h4>
        <button
          type="button"
          className="btn-close"
          data-bs-dismiss="modal"
          aria-label="Close"
        />
      </div>

      <div className="modal-body">

        {/* New Date-Time */}
        <div className="mb-3">
          <label className="form-label">
            New Interview Date & Time <span className="text-danger">*</span>
          </label>

         {(() => {
            const selectedDate = rescheduleData.new_datetime
              ? new Date(rescheduleData.new_datetime)
              : null;

            const now = new Date();

            return (
              <DatePicker
                selected={selectedDate}
                onChange={(date: Date | null) => {
                  setRescheduleData({
                    ...rescheduleData,
                    new_datetime: date ? date.toISOString() : "",
                  });

                  if (date) {
                    setRescheduleError((prev) => ({
                      ...prev,
                      new_datetime: "",
                    }));
                  }
                }}
                showTimeSelect
                showTimeInput
                dateFormat="dd-MMM-yyyy, hh:mm aa"
                placeholderText="Select interview date & time"
                className={`form-control ${
                  rescheduleError.new_datetime ? "is-invalid" : ""
                }`}

                /* Disable past dates */
                minDate={now}

                /* Disable past time when today */
                minTime={
                  selectedDate &&
                  selectedDate.toDateString() === now.toDateString()
                    ? now
                    : new Date(0, 0, 0, 0, 0, 0)
                }

                maxTime={new Date(0, 0, 0, 23, 59, 59)}

                customTimeInput={
                                <CustomTimeInput
                                  date={selectedDate ?? now}
                                  timeString={
                                    selectedDate
                                      ? selectedDate.toLocaleTimeString("en-GB", {
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        })
                                      : ""
                                  }
                                  onChange={(value: string) => {
                                    if (!selectedDate) return;

                                    const [hh, mm] = value.split(":").map(Number);

                                    const updated = new Date(selectedDate);
                                    updated.setHours(hh, mm, 0, 0);

                                    const nowLocal = new Date();

                                    // Prevent selecting past times from the time list
                                    if (
                                      updated.toDateString() === nowLocal.toDateString() &&
                                      updated < nowLocal
                                    ) {
                                      return;
                                    }
                                    setRescheduleData({
                                      ...rescheduleData,
                                      new_datetime: updated.toISOString(),
                                    });
                                  }}
                                />
                              }
              />
            );
          })()}

          {/* {rescheduleError.new_datetime && (
            <div className="invalid-feedback">
              {rescheduleError.new_datetime}
            </div>
          )} */}
        </div>

        {/* Reason */}
        <div className="mb-3">
          <label className="form-label">
            Reason for Reschedule <span className="text-danger">*</span>
          </label>

          <TextareaAutosize
            className={`form-control ${rescheduleError.reason ? "is-invalid" : ""}`}
            minRows={2}
            maxRows={4}
            value={rescheduleData.reason}
            onChange={(e) => {
              setRescheduleData({ ...rescheduleData, reason: e.target.value });
              setRescheduleError((prev) => ({ ...prev, reason: "" }));
            }}
          />

          {/* {rescheduleError.reason && (
            <div className="invalid-feedback">
              {rescheduleError.reason}
            </div>
          )} */}
        </div>

        {/* Meeting Link */}
        <div className="mb-3">
          <label className="form-label">
            Meeting Link <span className="text-danger">*</span>
          </label>

          <input
            type="url"
            className={`form-control ${rescheduleError.meeting_link ? "is-invalid" : ""}`}
            value={rescheduleData.meeting_link}
            onChange={(e) => {
              setRescheduleData({
                ...rescheduleData,
                meeting_link: e.target.value,
              });

              setRescheduleError((prev) => ({
                ...prev,
                meeting_link: "",
              }));
            }}
          />

          {/* {rescheduleError.meeting_link && (
            <div className="invalid-feedback">
              {rescheduleError.meeting_link}
            </div>
          )} */}
        </div>

      </div>

      <div className="modal-footer">
        <button
          type="button"
          className="btn btn-light me-2"
          data-bs-dismiss="modal"
        >
          Cancel
        </button>

        <button className="btn btn-primary" onClick={submitReschedule} disabled={isSaving} >
                  {isSaving ? "Saving..." : "Save"}
          </button>
      </div>

    </div>
  </div>
</div>

 {/* Schedule Interview Modal */}
      <ScheduleInterviewModal
      show={showInterviewModal}
      onClose={handleCloseModal}
      jobId={selectedJob?.id ?? 0} 
      jobProfile={selectedJob?.job_profile ?? ""} 
      interviewId={interviewId}
      mode={interviewMode}
      initialData={{
        candidateId: selectedCandidate?.id ?? undefined, // undefined if new candidate
        candidateName: selectedCandidate?.name ?? "",
        candidateEmail: selectedCandidate?.email ?? "",
        totalExperienceMonths: selectedCandidate?.total_experience_months ?? 0,
        currentCTC: selectedCandidate?.ctc_info ?? "",
        expectedCTC: selectedCandidate?.expected_ctc ?? "",
        currentLocation: selectedCandidate?.current_location ?? "",
        resumeFileName: selectedCandidate?.file_name ?? "",
      }}
      onSuccess={handleSuccess}
    />

      <Footer />
    </div>
    <ToastContainer position="top-right" autoClose={3000} /></>
  );
};
export default Interviews;